/**
 * Small, dependency-free WCAG contrast helpers.
 *
 * Buyers pick arbitrary brand colours in `src/config/site.config.ts`
 * (`theme.colors`). Some UI (e.g. `.btn-primary`) renders text *on top of*
 * `--brand-accent`/`--brand-primary`, so instead of hardcoding a fixed text
 * colour that can fail WCAG contrast for a given brand colour, `applyTheme()`
 * (see `src/config/site.config.ts`) uses `getReadableTextColor()` to pick
 * black or white — whichever yields the higher (and, in practice, an
 * AA-passing) contrast ratio against that colour.
 *
 * See docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md §9 ("SEO, Accessibility &
 * Performance") for the accessibility audit this addresses.
 */

/** Parses `#rgb`, `#rrggbb` (with or without `#`) into 0-255 channel values. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const normalized = hex.trim().replace(/^#/, '');
    const full =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : normalized;

    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

    return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
    };
}

/** Converts an sRGB 0-255 channel value into its linear-light counterpart. */
function toLinearChannel(channel255: number): number {
    const c = channel255 / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance (0 = black, 1 = white) for a `#rrggbb` colour. */
export function getRelativeLuminance(hex: string): number {
    const rgb = hexToRgb(hex);
    if (!rgb) return 1; // fail open to "treat as light" (picks black text)

    const r = toLinearChannel(rgb.r);
    const g = toLinearChannel(rgb.g);
    const b = toLinearChannel(rgb.b);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio (1–21) between two `#rrggbb` colours. */
export function getContrastRatio(hexA: string, hexB: string): number {
    const l1 = getRelativeLuminance(hexA);
    const l2 = getRelativeLuminance(hexB);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Picks `#000000` or `#ffffff` — whichever gives the higher WCAG contrast
 * ratio against `backgroundHex`. Used to keep text readable on top of any
 * buyer-configured brand colour without hardcoding a single text colour.
 */
export function getReadableTextColor(backgroundHex: string): '#000000' | '#ffffff' {
    const contrastWithBlack = getContrastRatio(backgroundHex, '#000000');
    const contrastWithWhite = getContrastRatio(backgroundHex, '#ffffff');
    return contrastWithBlack >= contrastWithWhite ? '#000000' : '#ffffff';
}
